import { supabase } from "@/integrations/supabase/client";
import type { AuthError, Session, User, Provider, UserAttributes } from "@supabase/supabase-js";
import { performComprehensiveLogout } from "@/utils/cacheUtils";

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface SignUpCredentials extends SignInCredentials {
  name?: string;
}

export interface ResetPasswordData {
  email: string;
}

export interface UpdatePasswordData {
  password: string;
}

export interface AuthResponse {
  session: Session | null;
  user: User | null;
  error?: string;
}

export const authService = {
  // Get current session
  getSession: async () => {
    return await supabase.auth.getSession();
  },

  // Get current user
  getCurrentUser: async (): Promise<User | null> => {
    const { data } = await supabase.auth.getUser();
    return data?.user || null;
  },

  // Auth state change listener
  onAuthStateChange: (
    callback: (event: any, session: Session | null) => void
  ) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Sign in with email and password
  signInWithEmail: async ({
    email,
    password,
  }: SignInCredentials): Promise<AuthResponse> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    return {
      session: data.session,
      user: data.user,
    };
  },

  // Sign in with OAuth provider
  signInWithOAuth: async (provider: Provider): Promise<void> => {
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
  },

  // Sign up with email and password
  signUpWithEmail: async ({
    email,
    password,
    name,
  }: SignUpCredentials): Promise<AuthResponse> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
        emailRedirectTo: `https://app.world-class.design/onboarding`,
      },
    });

    if (error) {
      throw error;
    }

    return {
      session: data.session,
      user: data.user,
    };
  },

  // Sign out
  signOut: async (): Promise<void> => {
    try {
      // Use comprehensive logout for better cross-tab synchronization
      await performComprehensiveLogout();
    } catch (error) {
      // Fallback to basic signout if comprehensive logout fails
      console.warn('Comprehensive logout failed, falling back to basic signout:', error);
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        throw signOutError;
      }
    }
  },

  // Reset password (send reset email)
  resetPassword: async ({ email }: ResetPasswordData): Promise<void> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      throw error;
    }
  },

  // Update password
  updatePassword: async ({ password }: UpdatePasswordData): Promise<void> => {
    const { error } = await supabase.auth.updateUser({
      password,
    });

    if (error) {
      throw error;
    }
  },

  // Check if user is admin
  isAdmin: async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", userId)
      .single();

    if (error) {
      throw error;
    }

    return !!data?.is_admin;
  },

  // Update user email
  async updateEmail(email: string): Promise<{ error?: string }> {
    const { error } = await supabase.auth.updateUser({ 
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth?type=email_change&next=/dashboard/settings`
      }
    } as UserAttributes);
    if (error) return { error: error.message };
    return {};
  },

  // Check if user is superadmin
  async checkSuperadminStatus(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "superadmin")
      .single();

    if (error) return false;
    return !!data;
  },

  // Refresh session
  async refreshSession(): Promise<{ error?: string }> {
    const { error } = await supabase.auth.refreshSession();
    if (error) return { error: error.message };
    return {};
  },
};

// Magic Link Authentication Types
export interface MagicLinkRequest {
  email: string;
  redirectTo?: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
  error?: string;
  reset_at?: string;
}

// Magic Link Authentication Methods
export const magicLinkService = {
  // Send magic link
  sendMagicLink: async ({ email, redirectTo = '/dashboard' }: MagicLinkRequest): Promise<MagicLinkResponse> => {
    const { data, error } = await supabase.functions.invoke('send-magic-link', {
      body: { email, redirect_to: redirectTo }
    });

    if (error) {
      throw new Error(error.message || 'Failed to send magic link');
    }

    return data;
  },

  // Verify magic link
  verifyMagicLink: async (token: string): Promise<AuthResponse> => {
    const { data, error } = await supabase.functions.invoke('verify-magic-link', {
      body: { token }
    });

    console.log('[verifyMagicLink] Edge Function response:', { data, error });

    if (error) {
      console.error('[verifyMagicLink] Edge Function error:', error);
      throw new Error(error.message || 'Edge Function returned a non-2xx status code');
    }

    if (!data.success) {
      console.error('[verifyMagicLink] Verification failed:', data);
      throw new Error(data?.message || 'Invalid magic link');
    }

    console.log('[verifyMagicLink] Received hashed token from Edge Function');

    // Verify OTP to create session
    console.log('[verifyMagicLink] Verifying OTP with hashed token...');
    const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
      token_hash: data.hashed_token,
      type: 'magiclink',
    });

    if (sessionError) {
      console.error('[verifyMagicLink] OTP verification error:', sessionError);
      throw sessionError;
    }

    console.log('[verifyMagicLink] OTP verified successfully:', sessionData);

    // Verify session is actually set by getting current session
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    if (!currentSession) {
      console.error('[verifyMagicLink] Session verification failed - no current session');
      throw new Error('Failed to establish session');
    }

    console.log('[verifyMagicLink] Session verified successfully');

    return {
      session: sessionData.session,
      user: sessionData.user,
    };
  },
};
